import React from "react"
import {useTranslation} from "react-i18next"

// Context
import {useUiSkin} from "../../../../ui-skin/context/UiSkinContext"

// Constants
import {UiSkin} from "../../../../ui-skin/constants"
import {isActiveLanguage, languageLabel, supportedLanguages} from "../../../../../translations/languages"

// Components
import Icon from "../../../../../components/modern/Icon"
import BrandMark from "../../../../../components/modern/BrandMark"
import LoginEcg from "../LoginEcg"
import ModernSignInForm from "../ModernSignInForm"

// Styles
import useStyles from "./styles"

const SKINS: Array<{value: UiSkin, labelKey: string}> = [
    {value: "classic", labelKey: "modern.classic"},
    {value: "modern", labelKey: "modern.modern"},
];

/**
 * Modern sign-in screen — the prototype's split login: gradient aside with the
 * wordmark and a decorative ECG on the left, the sign-in card on the right,
 * collapsing to the card alone under 880px.
 *
 * Renders NO <ModernAppBar/>: this is a pre-auth screen, so there is no user
 * block, no sign out and no dashboard link to go back to. The controls that
 * still make sense before signing in — skin, reading-room mode and language —
 * are floated top-right exactly as the prototype does. <ModernShell/> already
 * supplies the page background and base typography.
 */
export default function ModernSignInPage() {
    const styles = useStyles();
    const [t, i18n] = useTranslation("common");
    const {skin, setSkin, isDark, toggleTheme} = useUiSkin();

    return (
      <div className={`${styles.shell} ${isDark ? styles.shell__dark : ""}`}>
          <aside className={`${styles.aside} ${isDark ? styles.aside__dark : ""}`} aria-hidden="true">
              <div className={styles.aside_inner}>
                  <BrandMark height={30} className={styles.aside_brand}/>
                  <p className={styles.aside_tag}>
                      {t("modern.sign_in_tagline", "ECG review workspace for clinicians")}
                  </p>
                  <LoginEcg className={styles.aside_ecg}/>
              </div>
          </aside>
          <div className={styles.topbar}>
              <div className={styles.uimode} role="group" aria-label={t("modern.view_mode")}>
                  {SKINS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.uimode_btn} ${skin === option.value ? styles.uimode_btn__active : ""}`}
                      aria-pressed={skin === option.value}
                      onClick={() => setSkin(option.value)}
                    >
                        {t(option.labelKey)}
                    </button>
                  ))}
              </div>

              <button
                type="button"
                className={`${styles.iconbtn} ${isDark ? styles.iconbtn__active : ""}`}
                onClick={toggleTheme}
                aria-pressed={isDark}
                title={t("modern.reading_room")}
                aria-label={t("modern.reading_room")}
              >
                  <Icon name="moon"/>
              </button>

              <div className={styles.lang} role="group" aria-label={t("modern.language")}>
                  {supportedLanguages.map(language => (
                    <button
                      key={language}
                      type="button"
                      className={`${styles.langbtn} ${isActiveLanguage(i18n.language, language) ? styles.langbtn__active : ""}`}
                      aria-pressed={isActiveLanguage(i18n.language, language)}
                      onClick={() => i18n.changeLanguage(language)}
                    >
                        {languageLabel(language)}
                    </button>
                  ))}
              </div>
          </div>

          <main className={styles.main}>
              <ModernSignInForm/>
          </main>
      </div>
    )
}
